import React, { useState, useEffect } from 'react';
import {
    Mail,
    MailCheck,
    MailX,
    TrendingUp,
    RefreshCw,
    AlertCircle,
    Clock,
    BarChart3,
    Send
} from 'lucide-react';
import { apiClient, EmailAnalyticsResponse } from '../services/api';

interface EmailAnalyticsProps {
    userRole: string;
}

const EmailAnalytics: React.FC<EmailAnalyticsProps> = ({ userRole }) => {
    const [analytics, setAnalytics] = useState<EmailAnalyticsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    useEffect(() => {
        loadEmailAnalytics();
        // Auto-refresh every 60 seconds (less frequent than system health)
        const interval = setInterval(loadEmailAnalytics, 60000);
        return () => clearInterval(interval);
    }, []);

    const loadEmailAnalytics = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await apiClient.getEmailAnalytics();
            setAnalytics(response);
            setLastRefresh(new Date());
        } catch (err: any) {
            setError(err.message || 'Failed to load email analytics');
            console.error('Email analytics error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'delivered':
                return 'text-green-800 bg-green-50 border-green-200';
            case 'sent':
                return 'text-blue-800 bg-blue-50 border-blue-200';
            case 'pending':
                return 'text-amber-800 bg-amber-50 border-amber-200';
            case 'bounced':
            case 'dropped':
            case 'spam':
                return 'text-red-800 bg-red-50 border-red-200';
            case 'deferred':
                return 'text-yellow-800 bg-yellow-50 border-yellow-200';
            case 'failed':
                return 'text-red-800 bg-red-50 border-red-200';
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getEmailTypeLabel = (type: string) => {
        switch (type) {
            case 'consent_request':
            case 'Consent Request':
                return 'Consent Request';
            case 'consent_confirmation':
            case 'Consent Confirmed':
                return 'Consent Confirmed';
            case 'consent_declined':
            case 'Consent Declined':
                return 'Consent Declined';
            case 'consent_declined_confirmation':
                return 'Consent Declined Confirmation';
            case 'consent_withdrawn':
            case 'Consent Withdrawn':
            case 'driver_consent_withdrawn':
                return 'Consent Withdrawn';
            case 'consent_reminder':
                return 'Consent Reminder';
            case 'consent_expiry':
                return 'Consent Expiry';
            case 'consent_provided':
                return 'Consent Provided';
            case 'expiry_warning':
            case 'licence_expiry':
                return 'Licence Expiry';
            case 'daily_digest':
                return 'Daily Digest';
            case 'bulk_operation':
                return 'Bulk Operation';
            case 'compliance_alert':
                return 'Compliance Alert';
            case 'password_reset':
                return 'Password Reset';
            case 'report_ready':
                return 'Report Ready';
            case 'admin_notification':
                return 'Admin Notification';
            default:
                return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (isLoading && !analytics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#69BE28' }} />
                    <span className="text-gray-600">Loading email analytics...</span>
                </div>
            </div>
        );
    }

    if (error && !analytics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Email Analytics</h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={loadEmailAnalytics}
                        className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* Print Header */}
            <div className="hidden print:block border-b-2 pb-4 mb-8" style={{ borderColor: '#231f20' }}>
                <div className="flex items-center justify-between">
                    <img 
                        src="/logo-horizontal-black.png" 
                        alt="IntelliDrive" 
                        className="h-20 w-auto"
                    />
                    <div className="text-right">
                        <h1 className="text-lg font-bold text-gray-900">
                            IntelliDrive Compliance Dashboard
                        </h1>
                        <p className="text-sm text-gray-600">
                            Generated: {new Date().toLocaleDateString('en-GB')}
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Email Analytics Header */}
            <div className="mb-6">
                <div className="flex items-start justify-between mb-2">
                    <div className="w-1/2 sm:flex-1">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Email Delivery Analytics</h2>
                    </div>
                    <button
                        onClick={loadEmailAnalytics}
                        disabled={isLoading}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-md disabled:opacity-50 flex-shrink-0 ml-2"
                        title="Refresh Analytics"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} style={{ color: '#69BE28' }} />
                    </button>
                </div>
                <p className="text-sm sm:text-base text-gray-600 w-3/4 sm:w-full">
                    Monitor email delivery success rates and performance
                </p>
                <span className="text-xs sm:text-sm text-gray-500">
                    Last updated: {formatTime(lastRefresh.toISOString())}
                </span>
            </div>

            {analytics && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Total Emails */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 rounded-md mr-4" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                                    <Mail className="w-6 h-6" style={{ color: '#69BE28' }} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Total Emails</p>
                                    <p className="text-2xl font-bold text-gray-900">{analytics.data.summary.totalEmails}</p>
                                </div>
                            </div>
                        </div>

                        {/* Delivery Rate */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-md mr-4">
                                    <TrendingUp className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        {analytics.data.summary.deliveryRate.toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Successful Deliveries */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-md mr-4">
                                    <MailCheck className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Delivered</p>
                                    <p className="text-2xl font-bold text-gray-900">{analytics.data.summary.deliveredEmails}</p>
                                </div>
                            </div>
                        </div>

                        {/* Failed Deliveries */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-red-100 rounded-md mr-4">
                                    <MailX className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Failed</p>
                                    <p className="text-2xl font-bold text-gray-900">{analytics.data.summary.failedEmails}</p>
                                    <p className="text-xs text-gray-500">
                                        Avg {analytics.data.summary.avgRetryCount.toFixed(1)} retries
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Status Breakdown */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <BarChart3 className="w-5 h-5 mr-2" />
                                Delivery Status
                            </h3>
                            <div className="space-y-3">
                                {Object.entries(analytics.data.statusBreakdown)
                                    .sort(([, a], [, b]) => (b as number) - (a as number))
                                    .map(([status, count]) => {
                                        const pct = analytics.data.summary.totalEmails > 0
                                            ? Math.round(((count as number) / analytics.data.summary.totalEmails) * 100)
                                            : 0;
                                        return (
                                            <div key={status}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(status)}`}>
                                                        {status}
                                                    </span>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                                                        <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                                    <div
                                                        className="h-1.5 rounded-full transition-all duration-300"
                                                        style={{ backgroundColor: '#69BE28', width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>

                        {/* Email Type Breakdown */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <Send className="w-5 h-5 mr-2" />
                                Email Type Breakdown
                            </h3>
                            <div className="space-y-3">
                                {Object.entries(analytics.data.typeBreakdown)
                                    .sort(([, a], [, b]) => (b as number) - (a as number))
                                    .map(([type, count]) => {
                                        const pct = analytics.data.summary.totalEmails > 0
                                            ? Math.round(((count as number) / analytics.data.summary.totalEmails) * 100)
                                            : 0;
                                        return (
                                            <div key={type}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-700">{getEmailTypeLabel(type)}</span>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                                                        <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                                    <div
                                                        className="h-1.5 rounded-full transition-all duration-300"
                                                        style={{ backgroundColor: '#69BE28', width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>

                    {/* Recent Email Activity */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                            <Clock className="w-5 h-5 mr-2" />
                            Recent Email Activity
                        </h3>
                        
                        {analytics.data.recentLogs.length === 0 ? (
                            <div className="text-center py-8">
                                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">No recent email activity</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Recipient
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Type
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Retries
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Sent Date
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {analytics.data.recentLogs.map((log) => (
                                            <tr key={log.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="text-gray-900 font-medium">
                                                        {log.recipientEmail}
                                                    </div>
                                                    {log.bounceReason && (
                                                        <div className="text-xs text-red-600 mt-1">
                                                            {log.bounceReason}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {getEmailTypeLabel(log.emailType)}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {log.retryCount > 0 ? (
                                                        <span className="text-yellow-600 font-medium">
                                                            {log.retryCount}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-500">0</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    <div>{formatDate(log.sentAt)}</div>
                                                    <div className="text-xs">{formatTime(log.sentAt)}</div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(log.deliveryStatus)}`}>
                                                        {log.deliveryStatus === 'sent' ? 'delivered' : log.deliveryStatus}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default EmailAnalytics;